import { useNativeBridge } from './useNativeBridge';

export namespace Device {
    export interface DoNReqIPInsideEXDataResponse {
        mac?: string;
        wdata?: string;
        natip?: string;
        mduinfo?: string;
    }

    export interface PhoneBookInfo {
        name: string;
        number: string;
    }

    export interface DoNPhoneBookListResponse {
        result: string;
    }

    export interface ImageInfo {
        img: string;
    }

    export interface DoNCameraResponse {
        value?: ImageInfo[];
    }

    export interface DoNGalleryResponse {
        value?: ImageInfo[];
    }

    export interface DoNCameraProfileResponse {
        value?: ImageInfo[];
    }
}

/**
 * 디바이스 제어 관련 네이티브 브릿지 hook 함수
 * @returns
 */
export const useDeviceBridge = () => {
    const nativeBridge = useNativeBridge();

    /**
     * IOS전용 - Usim Mo 등록
     * @param workID
     * @param telNum
     */
    const doNRegistUsimMo = async (workID: string, telNum: string) => {
        const res = await nativeBridge<{ workID: string; telNum: string }, { result: string }>(
            'doNRegistUsimMo',
            { workID, telNum },
        );

        if (res.status === 'fail') {
            return;
        }

        return res.payload;
    };

    /**
     * IPInside 값 요청
     */
    const doNReqIPInsideEXData = async (): Promise<
        Device.DoNReqIPInsideEXDataResponse | undefined
    > => {
        const res = await nativeBridge<{}, Device.DoNReqIPInsideEXDataResponse>(
            'doNReqIPInsideEXData',
        );

        if (res.status === 'fail') {
            return;
        }

        const payload = res.payload as Device.DoNReqIPInsideEXDataResponse;

        const decodedPayload: Device.DoNReqIPInsideEXDataResponse = {
            ...payload,
            wdata: decodeURIComponent(payload?.wdata ?? ''),
            natip: decodeURIComponent(payload?.natip ?? ''),
            mduinfo: decodeURIComponent(payload?.mduinfo ?? ''),
        };

        return decodedPayload;
    };

    /**
     *
     */
    const doNPhoneBookList = async () => {
        const res = await nativeBridge<void, Device.DoNPhoneBookListResponse>('doNPhoneBookList');
        if (res.status === 'fail') {
            return;
        }

        const payload = res.payload as Device.DoNPhoneBookListResponse;
        const decodedResult = JSON.parse(decodeURIComponent(payload.result));

        return decodedResult;
    };

    /**
     * //TODO 응답 데이터 확인필요
     * @param returnType 0:경로, 1:이미지파일
     * @returns
     */
    const doNCamera = async (returnType: '0' | '1'): Promise<Device.ImageInfo[] | undefined> => {
        const res = await nativeBridge<{ returnType: '0' | '1' }, Device.DoNCameraResponse>(
            'doNCamera',
            {
                returnType,
            },
        );

        if (res.status === 'fail') {
            return;
        }

        const { payload } = res;
        if (!payload) {
            return;
        }

        return payload.value;
    };

    /**
     * 사진 선택
     * @param returnType
     * @returns
     */
    const doNGallery = async (returnType: '0' | '1'): Promise<Device.ImageInfo[] | undefined> => {
        const res = await nativeBridge<{ returnType: '0' | '1' }, Device.DoNGalleryResponse>(
            'doNGallery',
            {
                returnType,
            },
        );

        if (res.status === 'fail') {
            return;
        }

        const { payload } = res;
        if (!payload) {
            return;
        }

        return payload.value;
    };

    const doNMultiGallery = async (
        returnType: '0' | '1',
    ): Promise<Device.ImageInfo[] | undefined> => {
        const res = await nativeBridge<{ returnType: '0' | '1' }, Device.DoNGalleryResponse>(
            'doNMultiGallery',
            {
                returnType,
            },
        );

        if (res.status === 'fail') {
            return;
        }

        const { payload } = res;
        if (!payload) {
            return;
        }

        return payload.value;
    };

    /**
     * 디바이스 테마값 가져오기
     * @returns
     */
    const doNGetDeviceTheme = async () => {
        const res = await nativeBridge<void, { result: string }>('doNGetDeviceTheme');
        if (res.status === 'fail') {
            return;
        }

        return res.payload?.result;
    };

    /**
     * 전화걸기 요청
     * @param telNum 전화번호
     */
    const doNPhoneCall = async (telNum: string) => {
        nativeBridge('doNPhoneCall', { telNum }, { onlyRequest: true });
    };

    /**
     * 영상통화
     * @param authNum 인증번호
     * @returns
     */
    const doNFaceTime = async (authNum: string) => {
        const res = await nativeBridge<{ authNum: string }, { result: string }>('doNFaceTime', {
            authNum,
        });

        if (res.status === 'fail') {
            return;
        }

        const { payload } = res;
        if (!payload) {
            return;
        }

        return payload.result;
    };

    /**
     * 사진촬영 후 이미지 수정
     */
    const doNCameraProfile = async (
        returnType: '0' | '1',
    ): Promise<Device.ImageInfo[] | undefined> => {
        const res = await nativeBridge<{ returnType: '0' | '1' }, Device.DoNGalleryResponse>(
            'doNCameraProfile',
            {
                returnType,
            },
        );

        if (res.status === 'fail') {
            return;
        }

        const { payload } = res;
        if (!payload) {
            return;
        }

        return payload.value;
    };

    /**
     * 앨범선택 후 이미지 수정
     */
    const doNGalleryProfile = async (
        returnType: '0' | '1',
    ): Promise<Device.ImageInfo[] | undefined> => {
        const res = await nativeBridge<{ returnType: '0' | '1' }, Device.DoNGalleryResponse>(
            'doNGalleryProfile',
            {
                returnType,
            },
        );

        if (res.status === 'fail') {
            return;
        }

        const { payload } = res;
        if (!payload) {
            return;
        }

        return payload.value;
    };

    /**
     * 앱의 설정 화면으로 이동
     */
    const doNMoveSetting = async () => {
        await nativeBridge('doNMoveSetting', {}, { onlyRequest: true });
    };

    /**
     * SMS 보내기
     * @param loanMsg
     * @param telNum
     * @returns
     */
    const doNSendSMS = async (loanMsg: string, telNum: string) => {
        const res = await nativeBridge<{ loanMsg: string; telNum: string }, { result: string }>(
            'doNSendSMS',
            {
                loanMsg,
                telNum,
            },
        );

        if (res.status === 'fail') {
            return;
        }

        const { payload } = res;
        if (!payload) {
            return;
        }

        return payload.result;
    };

    /**
     * 이미지 다운로드 요청
     * @param inputJSON
     * @returns
     */
    const doNURLImageDownload = async (inputJSON: string) => {
        const res = await nativeBridge<{ inputJSON: string }, { result: number }>(
            'doNURLImageDownload',
            {
                inputJSON,
            },
        );

        if (res.status === 'fail') {
            return;
        }

        const { payload } = res;
        if (!payload) {
            return;
        }

        return payload.result;
    };

    /**
     * QR코드 스캔 - 적용메뉴: 모바일현금카드
     * @returns
     */
    const doNQrCodeScan = async () => {
        const res = await nativeBridge<void, { result: number }>('doNQrCodeScan');

        if (res.status === 'fail') {
            return;
        }

        const { payload } = res;
        if (!payload) {
            return;
        }

        return payload.result;
    };

    /**
     * 안면인식 촬영
     * @returns
     */
    const doNFaceRecognitionCamera = async () => {
        const res = await nativeBridge<void, { result: string }>('doNFaceRecognitionCamera');

        if (res.status === 'fail') {
            return;
        }

        const { payload } = res;
        if (!payload) {
            return;
        }

        return payload.result;
    };

    return {
        doNRegistUsimMo,
        doNReqIPInsideEXData,
        doNPhoneBookList,
        doNCamera,
        doNGallery,
        doNMultiGallery,
        doNPhoneCall,
        doNFaceTime,
        doNGetDeviceTheme,
        doNCameraProfile,
        doNGalleryProfile,
        doNMoveSetting,
        doNSendSMS,
        doNURLImageDownload,
        doNQrCodeScan,
        doNFaceRecognitionCamera,
    };
};
